import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { getChargebackDetail, updateChargeback, getCustomers, getContracts, updateChargebackLineItem, validateChargeback } from '../../config/api';
import { Save, X, ArrowLeft, FileText, AlertCircle, Calendar, DollarSign, User, Building, Package, Edit3, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';

const EditChargeback = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  console.log('EditChargeback component loaded with id:', id);
  console.log('User:', user);

  // Early return checks
  if (!id || id === 'undefined') {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-red-800 mb-2">Invalid Chargeback ID</h2>
          <p className="text-red-600 mb-4">The chargeback ID is missing or invalid.</p>
          <button
            onClick={() => navigate('/app/chargebacks')}
            title="Back to chargebacks"
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Back to Chargebacks
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-red-800 mb-2">Authentication Required</h2>
          <p className="text-red-600 mb-4">Please log in to edit chargebacks</p>
          <button
            onClick={() => navigate('/app/login')}
            title="Go to login"
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // State for form data
  const [formData, setFormData] = useState({
    claimNumber: '',
    claimDate: '',
    customerId: '',
    contractId: '',
    totalAmount: '',
    status: 'PENDING',
    errorReason: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Line items state
  const [editingLineItem, setEditingLineItem] = useState(null);
  const [lineItemErrors, setLineItemErrors] = useState({});
  const [validationResults, setValidationResults] = useState(null);

  // Fetch chargeback data
  const { data: chargeback, isLoading: chargebackLoading, error: chargebackError } = useQuery({
    queryKey: ['chargeback', id],
    queryFn: () => {
      console.log('Fetching chargeback with id:', id);
      return getChargebackDetail(id);
    },
    enabled: !!id && !!user,
    retry: 1
  });

  console.log('Chargeback loading state:', chargebackLoading);
  console.log('Chargeback error state:', chargebackError);
  console.log('Chargeback data:', chargeback);

  // Fetch customers for dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => getCustomers({ limit: 1000 }),
    enabled: !!user
  });

  // Fetch contracts for dropdown
  const { data: contractsData } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => getContracts(),
    enabled: !!user
  });

  // Update form data when chargeback loads
  useEffect(() => {
    if (chargeback) {
      console.log('Setting form data from chargeback:', chargeback);
      setFormData({
        claimNumber: chargeback.claimNumber || '',
        claimDate: chargeback.claimDate ? chargeback.claimDate.split('T')[0] : '',
        customerId: chargeback.customerId || '',
        contractId: chargeback.contractId || '',
        totalAmount: chargeback.totalAmount || '',
        status: chargeback.status || 'PENDING',
        errorReason: chargeback.errorReason || ''
      });
    }
  }, [chargeback]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateChargeback(id, data),
    onSuccess: (response) => {
      console.log('Update successful:', response);
      toast.success('Chargeback updated successfully');
      queryClient.invalidateQueries({ queryKey: ['chargeback', id] });
      queryClient.invalidateQueries({ queryKey: ['chargebacks'] });
      
      // Auto-validate after update
      validateMutation.mutate({ chargebackId: id, validationType: 'COMPREHENSIVE_VALIDATION' });
    },
    onError: (error) => {
      console.error('Update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update chargeback');
      setIsSubmitting(false);
    }
  });

  // Line item update mutation
  const updateLineItemMutation = useMutation({
    mutationFn: updateChargebackLineItem,
    onSuccess: (response) => {
      console.log('Line item update successful:', response);
      toast.success('Line item updated successfully');
      queryClient.invalidateQueries({ queryKey: ['chargeback', id] });
      setEditingLineItem(null);
      
      // Auto-validate after line item update
      validateMutation.mutate({ chargebackId: id, validationType: 'COMPREHENSIVE_VALIDATION' });
    },
    onError: (error) => {
      console.error('Line item update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update line item');
    }
  });

  // Validation mutation
  const validateMutation = useMutation({
    mutationFn: validateChargeback,
    onSuccess: (data) => {
      console.log('Validation successful:', data);
      toast.success('Chargeback validated successfully');
      queryClient.invalidateQueries({ queryKey: ['chargeback', id] });
      setValidationResults(data);
    },
    onError: (error) => {
      console.error('Validation error:', error);
      toast.error(error.response?.data?.message || 'Failed to validate chargeback');
    }
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      console.log('Submitting form data:', formData);
      await updateMutation.mutateAsync({ id, data: formData });
    } catch (error) {
      console.error('Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLineItemUpdate = async (lineItemId, updatedData) => {
    try {
      console.log('Updating line item:', lineItemId, updatedData);
      await updateLineItemMutation.mutateAsync({ lineItemId, data: updatedData });
    } catch (error) {
      console.error('Line item update error:', error);
    }
  };

  const handleValidate = () => {
    console.log('Manual validation triggered');
    validateMutation.mutate({ chargebackId: id, validationType: 'COMPREHENSIVE_VALIDATION' });
  };

  // Loading state
  if (chargebackLoading) {
    return (
      <div className="p-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
          <RefreshCw className="mx-auto h-12 w-12 text-blue-400 mb-4 animate-spin" />
          <h2 className="text-xl font-semibold text-blue-800 mb-2">Loading Chargeback</h2>
          <p className="text-blue-600">Please wait while we fetch the chargeback details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (chargebackError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-red-800 mb-2">Error Loading Chargeback</h2>
          <p className="text-red-600 mb-4">Failed to load chargeback details</p>
          <p className="text-sm text-red-500 mb-4">Error: {chargebackError.message}</p>
          <button
            onClick={() => navigate('/app/chargebacks')}
            title="Back to chargebacks"
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Back to Chargebacks
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!chargeback) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-yellow-400 mb-4" />
          <h2 className="text-xl font-semibold text-yellow-800 mb-2">No Data</h2>
          <p className="text-yellow-600 mb-4">No chargeback data available</p>
          <button
            onClick={() => navigate('/app/chargebacks')}
            title="Back to chargebacks"
            className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700"
          >
            Back to Chargebacks
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/app/chargebacks')}
              title="Back to chargebacks"
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Chargebacks</span>
            </button>
            <div className="h-6 w-px bg-gray-300"></div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Chargeback</h1>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleValidate}
              disabled={validateMutation.isPending}
              title="Validate chargeback"
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${validateMutation.isPending ? 'animate-spin' : ''}`} />
              <span>Validate</span>
            </button>
          </div>
        </div>

        {/* Validation Results */}
        {validationResults && (
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Validation Results</h3>
            
            {validationResults.isValid ? (
              <div className="flex items-center space-x-2 text-green-700">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Chargeback is valid</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2 text-red-700">
                <XCircle className="h-5 w-5" />
                <span className="font-medium">Chargeback has issues</span>
              </div>
            )}
            
            {validationResults.errors && validationResults.errors.length > 0 && (
              <div className="mt-3">
                <h4 className="text-sm font-medium text-red-800 mb-2">Issues Found:</h4>
                <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                  {validationResults.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {validationResults.warnings && validationResults.warnings.length > 0 && (
              <div className="mt-3">
                <h4 className="text-sm font-medium text-yellow-800 mb-2">Warnings:</h4>
                <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                  {validationResults.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Main Form */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Chargeback Details</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Claim Number
                  </label>
                  <input
                    type="text"
                    name="claimNumber"
                    value={formData.claimNumber}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter claim number"
                  />
                  {errors.claimNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.claimNumber}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Claim Date
                  </label>
                  <input
                    type="date"
                    name="claimDate"
                    value={formData.claimDate}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.claimDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.claimDate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer
                  </label>
                  <select
                    name="customerId"
                    value={formData.customerId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Customer</option>
                    {customersData?.customers?.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                  {errors.customerId && (
                    <p className="mt-1 text-sm text-red-600">{errors.customerId}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contract
                  </label>
                  <select
                    name="contractId"
                    value={formData.contractId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Contract</option>
                    {contractsData?.contracts?.map((contract) => (
                      <option key={contract.id} value={contract.id}>
                        {contract.number}{contract.name ? ` — ${contract.name}` : (contract.description ? ` — ${contract.description}` : '')}
                      </option>
                    ))}
                  </select>
                  {errors.contractId && (
                    <p className="mt-1 text-sm text-red-600">{errors.contractId}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="totalAmount"
                    value={formData.totalAmount}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                  {errors.totalAmount && (
                    <p className="mt-1 text-sm text-red-600">{errors.totalAmount}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="PROCESSED">Processed</option>
                  </select>
                  {errors.status && (
                    <p className="mt-1 text-sm text-red-600">{errors.status}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Error Reason
                </label>
                <textarea
                  name="errorReason"
                  value={formData.errorReason}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter error reason if applicable"
                />
                {errors.errorReason && (
                  <p className="mt-1 text-sm text-red-600">{errors.errorReason}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => navigate('/app/chargebacks')}
                  title="Cancel"
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || updateMutation.isPending}
                  title="Save changes"
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  <span>{isSubmitting || updateMutation.isPending ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
            </form>
          </div>

          {/* Line Items */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Line Items</h2>
            </div>
            <div className="p-6">
              {chargeback.lines && chargeback.lines.length > 0 ? (
                <div className="space-y-4">
                  {chargeback.lines.map((lineItem, index) => (
                    <div key={lineItem.id || index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-gray-900">
                          Line Item #{index + 1}
                        </h3>
                        <button
                          onClick={() => setEditingLineItem(editingLineItem === lineItem.id ? null : lineItem.id)}
                          title="Edit line item"
                          className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
                        >
                          <Edit3 className="h-4 w-4" />
                          <span>{editingLineItem === lineItem.id ? 'Cancel' : 'Edit'}</span>
                        </button>
                      </div>

                      {editingLineItem === lineItem.id ? (
                        <LineItemEditForm
                          lineItem={lineItem}
                          onSave={(updatedData) => handleLineItemUpdate(lineItem.id, updatedData)}
                          onCancel={() => setEditingLineItem(null)}
                          isLoading={updateLineItemMutation.isPending}
                        />
                      ) : (
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-600">Product:</span>
                            <p className="text-gray-900">{lineItem.productName || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Quantity:</span>
                            <p className="text-gray-900">{lineItem.quantity || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Sales Price:</span>
                            <p className="text-gray-900">{lineItem.salesPrice != null && lineItem.salesPrice !== '' ? '$' + Number(lineItem.salesPrice).toFixed(2) : 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Contract Price:</span>
                            <p className="text-gray-900">{lineItem.contractPrice != null && lineItem.contractPrice !== '' ? '$' + Number(lineItem.contractPrice).toFixed(2) : 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Status:</span>
                            <p className="text-gray-900">{lineItem.validationStatus || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600">Valid:</span>
                            <p className={`font-medium ${(lineItem.isValid || lineItem.validationStatus === 'VALIDATED') ? 'text-green-600' : 'text-red-600'}`}>
                              {(lineItem.isValid || lineItem.validationStatus === 'VALIDATED') ? 'Yes' : 'No'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p>No line items found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Line Item Edit Form Component
const LineItemEditForm = ({ lineItem, onSave, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    quantity: lineItem.quantity || '',
    salesPrice: lineItem.salesPrice || '',
    contractPrice: lineItem.contractPrice || '',
    notes: lineItem.notes || ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Quantity
          </label>
          <input
            type="number"
            name="quantity"
            value={formData.quantity}
            onChange={handleInputChange}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Sales Price
          </label>
          <input
            type="number"
            step="0.01"
            name="salesPrice"
            value={formData.salesPrice}
            onChange={handleInputChange}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Contract Price
          </label>
          <input
            type="number"
            step="0.01"
            name="contractPrice"
            value={formData.contractPrice}
            onChange={handleInputChange}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Notes
          </label>
          <input
            type="text"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          title="Cancel"
          className="px-3 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          title="Save"
          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
};

// Error boundary wrapper
const EditChargebackWithErrorBoundary = () => {
  try {
    return <EditChargeback />;
  } catch (error) {
    console.error('EditChargeback rendering error:', error);
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-red-800 mb-2">Component Error</h2>
          <p className="text-red-600 mb-4">An error occurred while rendering the edit form</p>
          <p className="text-sm text-red-500 mb-4">Error: {error.message}</p>
          <button
            onClick={() => window.location.reload()}
            title="Reload page"
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }
};

export default EditChargebackWithErrorBoundary;